import type {
  TracesListParams,
  TracesListResponse,
  TraceData,
  ErrorsListParams,
  ErrorsListResponse,
  ErrorGroupWithOccurrences,
  ErrorGroup,
  ErrorStatus,
  ErrorSeverity,
  SyncResponse,
  TestCasesListParams,
  TestCasesListResponse,
  TestCaseWithRuns,
  TestCase,
  CreateTestCaseParams,
  UpdateTestCaseParams,
  TestCaseRunResponse,
  Stats,
  StatsParams,
  TimelineData,
  TimelineParams,
  QuickStats,
  AnalyticsStats,
  LinearIssue,
  LinearStatus,
  HealthResponse,
  AutomationsListParams,
  AutomationsListResponse,
  AutomationWithStats,
  AutomationStats,
  AutomationStatsParams,
  CreateAutomationParams,
  UpdateAutomationParams,
  Automation,
  AutomationStatus,
  ActivityListParams,
  ActivityListResponse,
  // User types
  User,
  UsersListResponse,
  CreateUserParams,
  UpdateUserParams,
  UserPreferences,
  // Error enhancement types
  ErrorGroupExtended,
  ErrorComment,
  AssignErrorParams,
  BulkUpdateErrorsParams,
  CreateErrorCommentParams,
  ErrorSearchResult,
  // Alert types
  AlertRule,
  Alert,
  AlertMute,
  AlertRulesListResponse,
  AlertHistoryListParams,
  AlertHistoryListResponse,
  AlertMutesListResponse,
  AlertStats,
  CreateAlertRuleParams,
  UpdateAlertRuleParams,
  CreateAlertMuteParams,
  // Ticket types
  Ticket,
  TicketComment,
  TicketActivity,
  TicketsListParams,
  TicketsListResponse,
  CreateTicketParams,
  UpdateTicketParams,
  CreateTicketCommentParams,
  TicketStatus,
  // Test suite types
  TestSuite,
  TestSuiteRun,
  TestSuitesListParams,
  TestSuitesListResponse,
  TestSuiteRunsListParams,
  TestSuiteRunsListResponse,
  CreateTestSuiteParams,
  UpdateTestSuiteParams,
  UpdateTestSuiteScheduleParams,
  AddTestCasesToSuiteParams,
  ReorderTestCasesParams,
  CoverageOverview,
  FlakyTestsListResponse,
} from './types'

export interface AutomationClientConfig {
  baseUrl: string
  headers?: Record<string, string>
  onError?: (error: Error) => void
}

function buildQueryString<T extends object>(params: T): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue

    if (Array.isArray(value)) {
      // Append each array item with the same key (e.g., types=a&types=b)
      for (const item of value) {
        searchParams.append(key, String(item))
      }
    } else {
      searchParams.set(key, String(value))
    }
  }
  const str = searchParams.toString()
  return str ? `?${str}` : ''
}

export function createAutomationClient(config: AutomationClientConfig) {
  const { baseUrl, headers: defaultHeaders = {}, onError } = config

  async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...defaultHeaders,
        ...options?.headers,
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }))
      const err = new Error(error.message || `API error: ${response.status}`)
      onError?.(err)
      throw err
    }

    return response.json()
  }

  return {
    // Traces
    traces: {
      list: (params?: TracesListParams) =>
        fetchApi<TracesListResponse>(`/api/traces${buildQueryString(params || {})}`),

      get: (runId: string) => fetchApi<TraceData>(`/api/traces/${runId}`),
    },

    // Errors
    errors: {
      list: (params?: ErrorsListParams) =>
        fetchApi<ErrorsListResponse>(`/api/errors${buildQueryString(params || {})}`),

      get: (id: string) => fetchApi<ErrorGroupWithOccurrences>(`/api/errors/${id}`),

      updateStatus: (id: string, status: ErrorStatus) =>
        fetchApi<ErrorGroup>(`/api/errors/${id}/status`, {
          method: 'POST',
          body: JSON.stringify({ status }),
        }),

      updateSeverity: (id: string, severity: ErrorSeverity) =>
        fetchApi<ErrorGroup>(`/api/errors/${id}/severity`, {
          method: 'POST',
          body: JSON.stringify({ severity }),
        }),

      sync: () => fetchApi<SyncResponse>('/api/errors/sync', { method: 'POST' }),
    },

    // Test Cases
    testCases: {
      list: (params?: TestCasesListParams) =>
        fetchApi<TestCasesListResponse>(`/api/test-cases${buildQueryString(params || {})}`),

      get: (id: string) => fetchApi<TestCaseWithRuns>(`/api/test-cases/${id}`),

      create: (data: CreateTestCaseParams) =>
        fetchApi<TestCase>('/api/test-cases', {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      update: (id: string, data: UpdateTestCaseParams) =>
        fetchApi<TestCase>(`/api/test-cases/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),

      delete: (id: string) =>
        fetchApi<{ success: boolean }>(`/api/test-cases/${id}`, { method: 'DELETE' }),

      run: (id: string) =>
        fetchApi<TestCaseRunResponse>(`/api/test-cases/${id}/run`, { method: 'POST' }),
    },

    // Stats
    stats: {
      get: (params?: StatsParams) =>
        fetchApi<Stats>(`/api/stats${buildQueryString(params || {})}`),

      timeline: (params?: TimelineParams) =>
        fetchApi<TimelineData>(`/api/stats/timeline${buildQueryString(params || {})}`),

      // Fast local stats (< 100ms) - no external API calls
      quick: () => fetchApi<QuickStats>('/api/stats/quick'),

      // Pre-computed analytics from background job
      analytics: () => fetchApi<AnalyticsStats>('/api/stats/analytics'),
    },

    // Linear
    linear: {
      createIssue: (errorId: string) =>
        fetchApi<{ success: boolean; issue: LinearIssue }>(`/api/linear/errors/${errorId}`, {
          method: 'POST',
        }),

      status: () => fetchApi<LinearStatus>('/api/linear/status'),
    },

    // Health
    health: () => fetchApi<HealthResponse>('/health'),

    // Automations
    automations: {
      list: (params?: AutomationsListParams) =>
        fetchApi<AutomationsListResponse>(`/api/automations${buildQueryString(params || {})}`),

      get: (id: string) => fetchApi<AutomationWithStats>(`/api/automations/${id}`),

      create: (data: CreateAutomationParams) =>
        fetchApi<Automation>('/api/automations', {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      update: (id: string, data: UpdateAutomationParams) =>
        fetchApi<Automation>(`/api/automations/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),

      delete: (id: string) =>
        fetchApi<void>(`/api/automations/${id}`, { method: 'DELETE' }),

      trigger: (id: string, payload?: Record<string, unknown>) =>
        fetchApi<{ runId: string }>(`/api/automations/${id}/trigger`, {
          method: 'POST',
          body: JSON.stringify(payload || {}),
        }),

      updateStatus: (id: string, status: AutomationStatus) =>
        fetchApi<Automation>(`/api/automations/${id}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ status }),
        }),

      stats: (id: string, params?: AutomationStatsParams) =>
        fetchApi<AutomationStats>(`/api/automations/${id}/stats${buildQueryString(params || {})}`),
    },

    // Activity
    activity: {
      list: (params?: ActivityListParams) =>
        fetchApi<ActivityListResponse>(`/api/activity${buildQueryString(params || {})}`),
    },

    // Users
    users: {
      list: () => fetchApi<UsersListResponse>('/api/users'),

      get: (id: string) => fetchApi<User>(`/api/users/${id}`),

      me: () => fetchApi<User>('/api/users/me'),

      create: (data: CreateUserParams) =>
        fetchApi<User>('/api/users', {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      update: (id: string, data: UpdateUserParams) =>
        fetchApi<User>(`/api/users/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),

      updatePreferences: (data: UserPreferences) =>
        fetchApi<User>('/api/users/me/preferences', {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
    },

    // Enhanced Errors (extends existing errors namespace)
    errorEnhancements: {
      assign: (id: string, data: AssignErrorParams) =>
        fetchApi<ErrorGroupExtended>(`/api/errors/${id}/assign`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      bulkUpdate: (data: BulkUpdateErrorsParams) =>
        fetchApi<{ updated: number }>('/api/errors/bulk', {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      search: (q: string, params?: { limit?: number; offset?: number }) =>
        fetchApi<ErrorSearchResult>(
          `/api/errors/search${buildQueryString({ q, ...params })}`
        ),

      getComments: (id: string) =>
        fetchApi<{ comments: ErrorComment[] }>(`/api/errors/${id}/comments`),

      addComment: (id: string, data: CreateErrorCommentParams) =>
        fetchApi<ErrorComment>(`/api/errors/${id}/comments`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
    },

    // Alerts
    alerts: {
      rules: {
        list: () => fetchApi<AlertRulesListResponse>('/api/alerts/rules'),

        get: (id: string) => fetchApi<AlertRule>(`/api/alerts/rules/${id}`),

        create: (data: CreateAlertRuleParams) =>
          fetchApi<AlertRule>('/api/alerts/rules', {
            method: 'POST',
            body: JSON.stringify(data),
          }),

        update: (id: string, data: UpdateAlertRuleParams) =>
          fetchApi<AlertRule>(`/api/alerts/rules/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
          }),

        delete: (id: string) =>
          fetchApi<void>(`/api/alerts/rules/${id}`, { method: 'DELETE' }),

        test: (id: string) =>
          fetchApi<{ success: boolean; message: string }>(`/api/alerts/rules/${id}/test`, {
            method: 'POST',
          }),
      },

      history: {
        list: (params?: AlertHistoryListParams) =>
          fetchApi<AlertHistoryListResponse>(
            `/api/alerts/history${buildQueryString(params || {})}`
          ),

        get: (id: string) => fetchApi<Alert>(`/api/alerts/history/${id}`),

        acknowledge: (id: string, userId?: string) =>
          fetchApi<Alert>(`/api/alerts/history/${id}/acknowledge`, {
            method: 'POST',
            body: JSON.stringify({ userId }),
          }),

        resolve: (id: string) =>
          fetchApi<Alert>(`/api/alerts/history/${id}/resolve`, {
            method: 'POST',
          }),
      },

      mutes: {
        list: () => fetchApi<AlertMutesListResponse>('/api/alerts/mutes'),

        create: (data: CreateAlertMuteParams) =>
          fetchApi<AlertMute>('/api/alerts/mutes', {
            method: 'POST',
            body: JSON.stringify(data),
          }),

        delete: (id: string) =>
          fetchApi<void>(`/api/alerts/mutes/${id}`, { method: 'DELETE' }),
      },

      stats: () => fetchApi<AlertStats>('/api/alerts/stats'),
    },

    // Tickets
    tickets: {
      list: (params?: TicketsListParams) =>
        fetchApi<TicketsListResponse>(`/api/tickets${buildQueryString(params || {})}`),

      get: (id: string) => fetchApi<Ticket>(`/api/tickets/${id}`),

      create: (data: CreateTicketParams) =>
        fetchApi<Ticket>('/api/tickets', {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      update: (id: string, data: UpdateTicketParams) =>
        fetchApi<Ticket>(`/api/tickets/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),

      updateStatus: (id: string, status: TicketStatus) =>
        fetchApi<Ticket>(`/api/tickets/${id}/status`, {
          method: 'POST',
          body: JSON.stringify({ status }),
        }),

      assign: (id: string, assigneeId: string | null) =>
        fetchApi<Ticket>(`/api/tickets/${id}/assign`, {
          method: 'POST',
          body: JSON.stringify({ assigneeId }),
        }),

      addComment: (id: string, data: CreateTicketCommentParams) =>
        fetchApi<TicketComment>(`/api/tickets/${id}/comments`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      getActivity: (id: string) =>
        fetchApi<{ activities: TicketActivity[] }>(`/api/tickets/${id}/activity`),

      linkError: (id: string, errorGroupId: string) =>
        fetchApi<void>(`/api/tickets/${id}/link-error`, {
          method: 'POST',
          body: JSON.stringify({ errorGroupId }),
        }),

      linkTestCase: (id: string, testCaseId: string) =>
        fetchApi<void>(`/api/tickets/${id}/link-test-case`, {
          method: 'POST',
          body: JSON.stringify({ testCaseId }),
        }),

      syncToLinear: (id: string) =>
        fetchApi<Ticket>(`/api/tickets/${id}/sync-to-linear`, {
          method: 'POST',
        }),
    },

    // Test Suites
    testSuites: {
      list: (params?: TestSuitesListParams) =>
        fetchApi<TestSuitesListResponse>(
          `/api/test-suites${buildQueryString(params || {})}`
        ),

      get: (id: string) => fetchApi<TestSuite>(`/api/test-suites/${id}`),

      create: (data: CreateTestSuiteParams) =>
        fetchApi<TestSuite>('/api/test-suites', {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      update: (id: string, data: UpdateTestSuiteParams) =>
        fetchApi<TestSuite>(`/api/test-suites/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),

      delete: (id: string) =>
        fetchApi<void>(`/api/test-suites/${id}`, { method: 'DELETE' }),

      updateSchedule: (id: string, data: UpdateTestSuiteScheduleParams) =>
        fetchApi<TestSuite>(`/api/test-suites/${id}/schedule`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),

      addTestCases: (id: string, data: AddTestCasesToSuiteParams) =>
        fetchApi<TestSuite>(`/api/test-suites/${id}/test-cases`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      removeTestCase: (id: string, testCaseId: string) =>
        fetchApi<void>(`/api/test-suites/${id}/test-cases/${testCaseId}`, {
          method: 'DELETE',
        }),

      reorderTestCases: (id: string, data: ReorderTestCasesParams) =>
        fetchApi<TestSuite>(`/api/test-suites/${id}/test-cases/reorder`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),

      run: (id: string) =>
        fetchApi<TestSuiteRun>(`/api/test-suites/${id}/run`, {
          method: 'POST',
        }),

      getRuns: (id: string, params?: TestSuiteRunsListParams) =>
        fetchApi<TestSuiteRunsListResponse>(
          `/api/test-suites/${id}/runs${buildQueryString(params || {})}`
        ),

      getRun: (id: string, runId: string) =>
        fetchApi<TestSuiteRun>(`/api/test-suites/${id}/runs/${runId}`),

      // Flaky tests (across all test cases)
      flaky: () => fetchApi<FlakyTestsListResponse>('/api/test-suites/flaky'),

      // Coverage overview
      coverage: () => fetchApi<CoverageOverview>('/api/test-suites/coverage'),
    },
  }
}

export type AutomationClient = ReturnType<typeof createAutomationClient>
